import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { changeStaffPassword, changeClientPassword } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff } from 'lucide-react';

const ChangePassword = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const onChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.current_password || !formData.new_password || !formData.confirm_password) {
      toast.error('Plotëso të gjitha fushat');
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error('Fjalëkalimi i ri duhet të ketë të paktën 8 karaktere');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Konfirmimi i fjalëkalimit nuk përputhet');
      return;
    }

    if (formData.current_password === formData.new_password) {
      toast.error('Fjalëkalimi i ri duhet të jetë ndryshe nga aktuali');
      return;
    }

    try {
      setLoading(true);

      if (user?.role === 'BUSINESS' || user?.role === 'USER') {
        await changeClientPassword({
          current_password: formData.current_password,
          new_password: formData.new_password,
        });
      } else {
        await changeStaffPassword({
          current_password: formData.current_password,
          new_password: formData.new_password,
        });
      }

      toast.success('Fjalëkalimi u ndryshua me sukses');
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || 'Gabim në ndryshimin e fjalëkalimit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="change-password-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-outfit flex items-center gap-2">
          <Lock className="w-6 h-6 text-primary" />
          Ndrysho Fjalëkalimin
        </h1>
        <p className="text-muted-foreground mt-1">
          Për siguri, përdor një fjalëkalim të fortë me të paktën 8 karaktere.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Siguria e Llogarisë</CardTitle>
          <CardDescription>
            Vendos fjalëkalimin aktual dhe më pas fjalëkalimin e ri.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="current_password">Fjalëkalimi aktual</Label>
              <div className="relative">
                <Input
                  id="current_password"
                  type={showCurrent ? 'text' : 'password'}
                  value={formData.current_password}
                  onChange={(e) => onChange('current_password', e.target.value)}
                  placeholder="Vendos fjalëkalimin aktual"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">Fjalëkalimi i ri</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNew ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={(e) => onChange('new_password', e.target.value)}
                  placeholder="Vendos fjalëkalimin e ri"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Konfirmo fjalëkalimin e ri</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={(e) => onChange('confirm_password', e.target.value)}
                  placeholder="Përsërit fjalëkalimin e ri"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="change-password-submit">
              {loading ? 'Duke ruajtur...' : 'Ruaj fjalëkalimin e ri'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;
